import { GripVertical, CornerDownLeft, MoreHorizontal, Trash2, Clock } from '@/ui/icons'
import { WaypointResponse } from '@/shared/api/routes'
import {
  getWaypointTypeLabel,
  getWaypointTypeIconWithCustom,
  getWaypointTagVariant
} from '@/pages/itinerary-builder/utils/waypointTypeStyles'
import { formatCoordinatesForCopy } from '@/modules/trips/utils/coordinateUtils'
import { formatTimeForDisplay, calculateDurationMinutes, formatDuration } from '@/modules/trips/utils/timeCalculations'
import { cn } from '@/shared/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Button } from '@/ui/components'

export interface ItineraryWaypointRowProps {
  waypoint: WaypointResponse
  distanceFromPrevious: string | null
  dayNumber: number
  index: number
  isDetour?: boolean
  isSelected?: boolean
  onSelect?: (waypointId: string) => void
  dragAttributes?: Record<string, unknown>
  dragListeners?: Record<string, unknown>
  isDraggable?: boolean
  /** When true and onDeleteWaypoint is set, detour rows show an ellipsis actions menu */
  isOwner?: boolean
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
}

export function ItineraryWaypointRow({
  waypoint,
  distanceFromPrevious,
  dayNumber,
  index,
  isDetour = false,
  isSelected = false,
  onSelect,
  dragAttributes,
  dragListeners,
  isDraggable = false,
  isOwner = false,
  onDeleteWaypoint
}: ItineraryWaypointRowProps) {
  const TypeIcon = getWaypointTypeIconWithCustom(waypoint.poi_type ?? null, waypoint.poi_custom_label)
  const rawTypeLabel = getWaypointTypeLabel(waypoint.poi_type ?? null, waypoint.poi_custom_label)
  const typeLabel = !isDetour && (waypoint.poi_type === null || waypoint.poi_type === undefined)
    ? 'Waypoint'
    : rawTypeLabel
  const tagVariant = getWaypointTagVariant(waypoint.poi_type ?? null, isDetour)
  const locationText = waypoint.poi_address ?? (waypoint.latitude != null && waypoint.longitude != null
    ? formatCoordinatesForCopy(waypoint.latitude, waypoint.longitude)
    : null)
  const displayName =
    waypoint.name && waypoint.name.trim().length > 0 ? waypoint.name : 'Unnamed waypoint'

  const subtitleParts: string[] = []
  if (locationText) subtitleParts.push(locationText)
  if (distanceFromPrevious) subtitleParts.push(distanceFromPrevious)
  const subtitle = subtitleParts.join(' · ')

  const arrive = waypoint.start_time ? formatTimeForDisplay(waypoint.start_time) : null
  const leave = waypoint.end_time ? formatTimeForDisplay(waypoint.end_time) : null
  const durationMinutes = calculateDurationMinutes(waypoint.start_time, waypoint.end_time)
  const durationLabel = durationMinutes != null ? formatDuration(durationMinutes) : null
  const hasTimes = arrive != null || leave != null
  const timeSummary =
    hasTimes &&
    (arrive && leave
      ? `Arrive ${arrive} · Leave ${leave}${durationLabel != null ? ` · ${durationLabel}` : ''}`
      : arrive
        ? `Arrive ${arrive}`
        : leave
          ? `Leave by ${leave}`
          : '')

  const showActionsMenu = isDetour && isOwner && !!onDeleteWaypoint

  const handleClick = () => {
    if (onSelect) onSelect(waypoint.id)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDeleteWaypoint?.(waypoint.id)
  }

  const tagClassName = tagVariant === 'adventure'
    ? 'bg-adventure-100 text-adventure-800 dark:bg-adventure-900/50 dark:text-adventure-200'
    : 'bg-earth-200 text-earth-800 dark:bg-earth-700 dark:text-earth-200'

  return (
    <div
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? handleClick : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-md border border-earth-200/80 dark:border-earth-600/60 py-2 px-3 transition-colors',
        'bg-earth-50/50 dark:bg-earth-800/20',
        isDetour && 'ml-5 pl-0',
        onSelect && 'cursor-pointer hover:bg-earth-100/80 dark:hover:bg-earth-200/80',
        isSelected && 'ring-2 ring-adventure-500 bg-adventure-50 dark:bg-adventure-950/30 border-adventure-300'
      )}
    >
      {isDraggable && dragAttributes && dragListeners && (
        <div
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-earth-400 hover:text-earth-600"
          {...dragAttributes}
          {...dragListeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}

      {isDetour ? (
        <div className="flex flex-shrink-0 flex-col items-stretch w-4 min-h-[1.5rem]">
          <div className="flex-1 min-h-[0.25rem] border-l-2 border-adventure-500 rounded-b-none" />
          <div className="flex items-center justify-center h-4 w-4 bg-adventure-500 text-primary-foreground rounded-b rounded-r">
            <CornerDownLeft className="h-2.5 w-2.5" aria-hidden />
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
            'bg-adventure-500 text-primary-foreground dark:bg-adventure-600 dark:text-primary-foreground'
          )}
        >
          {index}
        </div>
      )}

      <div className="min-w-0 flex-1 min-h-0 flex flex-col justify-center py-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-earth-900 dark:text-foreground truncate" title={displayName}>
            {displayName}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium',
              tagClassName
            )}
          >
            <TypeIcon className="h-2.5 w-2.5 flex-shrink-0" />
            {typeLabel}
          </span>
        </div>
        {subtitle && (
          <div className="text-[11px] text-earth-600 dark:text-foreground truncate mt-0.5">
            {subtitle}
          </div>
        )}
        {hasTimes && timeSummary && (
          <div className="text-[11px] text-earth-600 dark:text-foreground mt-0.5 flex items-center gap-1">
            <Clock className="h-3 w-3 flex-shrink-0 text-earth-500 dark:text-foreground/80" aria-hidden />
            <span className="truncate">{timeSummary}</span>
          </div>
        )}
      </div>

      {showActionsMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0 text-earth-500 hover:text-earth-700"
              onClick={(e) => e.stopPropagation()}
              aria-label="Waypoint actions"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-destructive dark:text-destructive-foreground focus:bg-destructive/10 focus:text-destructive-foreground dark:focus:bg-destructive/30"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
